import { ParamMap } from '@angular/router';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { PageCriteriaModelInterface } from '../../../Common/Misc/models/page-criteria.model.interface';
import { CirculationFilterApiModel, TargetingSearchApi } from '../../../api/targeting/model/targeting-search.api.model';
import { OwnershipType } from '../../../api/targeting/enum/ownership.type.enum';
import { AttributeModel } from '../../../Common/Misc/models/attribute.model';
import { attributeTypesEnum } from '../../../Common/Misc/enums/attribute-types.enum';
import { IncludeExcludeModel } from '../../../Common/Misc/models/include-exclude-model';
import { TargetingSearchFacetsEnum } from '../../../api/targeting/enum/targeting-search-facets.enum';
import { SearchModeEnum } from '../../../Common/TargetingShared/enum/search-mode.enum';
import { BadgeFilterModel } from './badge-filter.model';
import { IdNamePairModel } from '../../../Common/Misc/models/id-name-pair.model';
import { MonitoringQueryApi } from '../../../api/targeting/model/monitoring-query.api.model';
import { GroupByEnum } from '../../../api/targeting/enum/group-by.enum';
import { BaseModel } from '../../../Common/Basic/models/base.model';
import { MentionSearchFacetTypesEnum } from '../../../api/mentions/enum/mention-search-facet-types.enum';
import { TargetingMediaTypeEnum } from '../enums/targeting-media-type.enum';

export interface RectMapQuery {
    lowerLeftCorner: string;
    upperRightCorner: string;
}
export class TargetingSearch extends BaseModel<TargetingSearch, TargetingSearchApi> implements AuditableModel, PageCriteriaModelInterface {

    static readonly MAX_SEARCH_SIZE = 10000;

    id?: number;
    name?: string;

    searchMode: SearchModeEnum;

    attributes: AttributeModel[];

    createdByUserId?: number;
    createdDate?: Date;

    modifiedByUserId?: number;
    modifiedDate?: Date;

    page: number;
    pageSize: number;

    refineQuery?: string;

    hidden?: boolean;

    hasActiveNotices?: boolean;

    subjects:        IncludeExcludeModel<number>;
    geographies:     IncludeExcludeModel<number>;
    areasOfCoverage: IncludeExcludeModel<number>;
    mediaMarkets:    IncludeExcludeModel<number>;
    list:            IncludeExcludeModel<number> = new IncludeExcludeModel<number>();
    distribution:    IncludeExcludeModel<number> = new IncludeExcludeModel<number>();
    jobRoles:        IncludeExcludeModel<number>;
    outletSubjects:  IncludeExcludeModel<number>;

    outletIdsNames:  IncludeExcludeModel<IdNamePairModel>;
    contactIdsNames: IncludeExcludeModel<IdNamePairModel>;

    outletMonitoringKeywords: IncludeExcludeModel<string>;
    languages:                IncludeExcludeModel<string>;
    jobTitles:                IncludeExcludeModel<string>;
    monitoringKeywords:       IncludeExcludeModel<string>;
    mediaTypes:               IncludeExcludeModel<TargetingMediaTypeEnum>;
    productTypes:             IncludeExcludeModel<string>;
    marketSectors:            IncludeExcludeModel<string>;
    coverageFocuses:          IncludeExcludeModel<string>;
    productFrequencies:       IncludeExcludeModel<string>;
    targetAudiences:          IncludeExcludeModel<string>;

    audienceReachFilter: CirculationFilterApiModel = {
        type: null,
        range: {
            min: null,
            max: null
        }
    };

    badgeFilters: BadgeFilterModel[] = [];

    advMonQuery: MonitoringQueryApi;

    rectangleQuery?: RectMapQuery;

    includePrivateContacts: boolean;
    privateContactsOnly: boolean = false;
    isRecommendedRecipientSearch: boolean = false;
    groupByOutlet: boolean;

    displayGroupId?: number;
    isFavorited: boolean = false;
    isFilterSearch?: boolean = false;
    expiryDate?: Date;

    private readonly simpleCompareFields = [
        'includePrivateContacts',
        'name',
        'searchMode',
        'groupByOutlet'
        // I don't know if these should be here because they're filter-only
        // TODO : verify
        // badgeFilters
    ];

    private readonly includeExcludeCompareFields = [
        'contactIdsNames',
        'outletIdsNames',
        'subjects',
        'monitoringKeywords',
        'outletSubjects',
        'outletMonitoringKeywords',
        'mediaMarkets',
        'jobRoles',
        'languages',
        'geographies',
        'areasOfCoverage',
        'jobTitles',
        'mediaTypes',
        'productTypes',
        'marketSectors',
        'coverageFocuses',
        'productFrequencies',
        'targetAudiences',
        'list',
        'distribution'
    ];

    private readonly searchRefinementCompareFields = [
        'mediaTypes',
        'productTypes',
        'marketSectors',
        'coverageFocuses',
        'productFrequencies',
        'targetAudiences',
    ];

    constructor(apiModel?: TargetingSearchApi) {
        super();
        if (apiModel) {
            this.constructFromApiModel(apiModel);
        } else {
            this.constructDefault();
        }
    }

    /**
     * @param base64 b64 encoded string of a stripped-down & serialized (backend) search criteria model.
     */
    static fromFilterQueryString(base64: string): { targetingSearch: TargetingSearch; distributionId: string } {
        const model: {
            targetingSearch: TargetingSearch;
            distributionId: string;
        } = {
            targetingSearch: null,
            distributionId: null
        };

        try {
            const filterModel = JSON.parse(decodeURIComponent(atob(base64)));
            model.distributionId = filterModel.distributionId;

            model.targetingSearch = new TargetingSearch(filterModel.targetingSearch);
        } catch (e) {
            // parsing json failed, could happen if the url is junk
            return null;
        }

        return model;
    }

    static constructFromRouteParam(filterMap?: ParamMap): TargetingSearch | null {
        if (!filterMap?.has('filters')) {
            return null;
        }

        const filtersQueryString = filterMap.get('filters');
        if (!filtersQueryString) {
            return null;
        }

        try {
            const model = TargetingSearch.fromFilterQueryString(filtersQueryString);
            return model?.targetingSearch ?? null;
        } catch (e) {
            return null;
        }
    }

    /**
     * @param model the model to by query-stringified
     *
     * The parameters omitted aren't relevant for filters, and just make the query string longer.
     */
    static toFilterQueryString(searchModel: TargetingSearch, distributionId?: number): string {
        if (!searchModel) {
            return null;
        }

        const model: {
            targetingSearch: TargetingSearchApi;
            distributionId?: number;
        } = {
            targetingSearch: searchModel.toApiModel()
        };

        if (distributionId != null) {
            model.distributionId = distributionId;
        }
        return btoa(
            encodeURIComponent(JSON.stringify(model))
        );
    }

    constructFromApiModel(apiModel: TargetingSearchApi) {
        this.id = apiModel.id;
        this.name = apiModel.name;

        this.refineQuery = apiModel.query?.refineQuery;

        this.includePrivateContacts = apiModel.query?.ownershipTypes.indexOf(OwnershipType.PRIVATE) > -1;
        this.searchMode = <SearchModeEnum>apiModel.query?.searchMode;
        this.groupByOutlet = (apiModel.query?.groupBy === GroupByEnum.OUTLET) || false;

        this.subjects = new IncludeExcludeModel(apiModel.query?.subject);
        this.monitoringKeywords = new IncludeExcludeModel(apiModel.query?.monitoringKeywords);
        this.outletSubjects = new IncludeExcludeModel(apiModel.query?.outletSubject);
        this.outletMonitoringKeywords = new IncludeExcludeModel(apiModel.query?.outletMonitoringKeywords);
        this.areasOfCoverage = new IncludeExcludeModel(apiModel.query?.areaOfCoverage);
        this.mediaMarkets = new IncludeExcludeModel(apiModel.query?.mediaMarket);
        this.geographies = new IncludeExcludeModel(apiModel.query?.geography);
        this.jobRoles = new IncludeExcludeModel(apiModel.query?.jobRole);
        this.jobTitles = new IncludeExcludeModel(apiModel.query?.jobTitle);
        this.languages = new IncludeExcludeModel(apiModel.query?.language);

        this.outletIdsNames = new IncludeExcludeModel<IdNamePairModel>({
            include: apiModel.query?.outletIdNamePair?.include?.map(x => new IdNamePairModel(x.name, x.id)) ?? [],
            exclude: apiModel.query?.outletIdNamePair?.exclude?.map(x => new IdNamePairModel(x.name, x.id)) ?? [],
            includeAll: []
        });

        this.contactIdsNames = new IncludeExcludeModel<IdNamePairModel>({
            include: apiModel.query?.contactIdNamePair?.include?.map(x => new IdNamePairModel(x.name, x.id)) ?? [],
            exclude: apiModel.query?.contactIdNamePair?.exclude?.map(x => new IdNamePairModel(x.name, x.id)) ?? [],
            includeAll: []
        });

        this.mediaTypes = new IncludeExcludeModel<TargetingMediaTypeEnum>(apiModel.query?.mediaType);
        this.productTypes = new IncludeExcludeModel(apiModel.query?.productType);
        this.marketSectors = new IncludeExcludeModel(apiModel.query?.marketSector);
        this.coverageFocuses = new IncludeExcludeModel(apiModel.query?.coverageFocus);
        this.productFrequencies = new IncludeExcludeModel(apiModel.query?.productFrequency);
        this.targetAudiences = new IncludeExcludeModel(apiModel.query?.targetAudience);

        this.rectangleQuery = apiModel.query?.rectangleQuery;

        this.hasActiveNotices = apiModel.query?.hasActiveNotices;
        this.advMonQuery = apiModel.query?.advMonQuery;
        this.expiryDate = apiModel?.expiryDate;
        this.isFilterSearch = apiModel?.isFilterSearch ?? false;

        this.displayGroupId = apiModel.displayGroupId;
        this.isFavorited = apiModel.isFavorited ?? false;

        this.createdByUserId = apiModel.createdUserId;
        this.createdDate = apiModel.createdDate;
        this.modifiedByUserId = apiModel.modifiedUserId;
        this.modifiedDate = apiModel.modifiedDate;
        this.hidden = apiModel.hidden;

        if (apiModel.query?.list) {
            this.list = new IncludeExcludeModel(apiModel.query.list);
        }

        if (apiModel.query?.badgeFilters) {
            this.badgeFilters = apiModel.query.badgeFilters;
        }

        if (apiModel.query?.distribution) {
            this.distribution = new IncludeExcludeModel(apiModel.query.distribution);
        }

        if (apiModel.query?.circulationFilter) {
            this.audienceReachFilter = apiModel.query.circulationFilter;
        }

        this.attributes = [
            {
                label: 'Discover.targeting.panels.createdUser',
                id: 0,
                value: apiModel.createdUserId,
                type: attributeTypesEnum.user
            },
            {
                label: 'Discover.targeting.panels.createdDate',
                id: 1,
                value: apiModel.createdDate,
                type: attributeTypesEnum.date
            },
            {
                label: 'Discover.targeting.panels.modifiedUser',
                id: 2,
                value: apiModel.modifiedUserId,
                type: attributeTypesEnum.user
            },
            {
                label: 'Discover.targeting.panels.modifiedDate',
                id: 3,
                value: apiModel.modifiedDate,
                type: attributeTypesEnum.date
            }
        ].filter(x => !!x.value);
    }

    constructDefault() {
        this.subjects        = new IncludeExcludeModel<number>();
        this.geographies     = new IncludeExcludeModel<number>();
        this.areasOfCoverage = new IncludeExcludeModel<number>();
        this.mediaMarkets    = new IncludeExcludeModel<number>();
        this.list            = new IncludeExcludeModel<number>();
        this.distribution    = new IncludeExcludeModel<number>();
        this.outletSubjects  = new IncludeExcludeModel<number>();
        this.jobRoles        = new IncludeExcludeModel<number>();

        this.outletIdsNames  = new IncludeExcludeModel<IdNamePairModel>();
        this.contactIdsNames = new IncludeExcludeModel<IdNamePairModel>();

        this.languages = new IncludeExcludeModel<string>();
        this.jobTitles = new IncludeExcludeModel<string>();

        this.outletMonitoringKeywords = new IncludeExcludeModel<string>();
        this.monitoringKeywords       = new IncludeExcludeModel<string>();
        this.mediaTypes               = new IncludeExcludeModel<TargetingMediaTypeEnum>();
        this.productTypes             = new IncludeExcludeModel<string>();
        this.marketSectors            = new IncludeExcludeModel<string>();
        this.coverageFocuses          = new IncludeExcludeModel<string>();
        this.productFrequencies       = new IncludeExcludeModel<string>();
        this.targetAudiences          = new IncludeExcludeModel<string>();

        this.audienceReachFilter = {
            type: null,
            range: {
                min: null,
                max: null
            }
        };

        this.badgeFilters = [];

        this.includePrivateContacts = true;
        this.privateContactsOnly = false;
        this.isRecommendedRecipientSearch = false;
        this.groupByOutlet = false;
        this.isFavorited = false;
    }

    toApiModel(facets: boolean = false, filters?: TargetingSearch): TargetingSearchApi {
        let ownershipTypes: OwnershipType[] = [OwnershipType.SYSTEM];
        if (this.includePrivateContacts) {
            ownershipTypes.push(OwnershipType.PRIVATE);
        }

        if (this.privateContactsOnly) {
            ownershipTypes = [OwnershipType.PRIVATE];
            this.searchMode = SearchModeEnum.contact;
        }
        // Keeping either name or id for contacts
        const contactIdNamePair = new IncludeExcludeModel<IdNamePairModel>();
        contactIdNamePair.include = this.contactIdsNames.toApiModel().include.map(contact =>
            (contact.id) ? new IdNamePairModel(undefined, contact.id) : new IdNamePairModel(contact.name));
        contactIdNamePair.exclude = this.contactIdsNames.toApiModel().exclude.map(contact =>
            (contact.id) ? new IdNamePairModel(undefined, contact.id) : new IdNamePairModel(contact.name));
        // Keeping either name or id for outlets
        const outletIdNamePair = new IncludeExcludeModel<IdNamePairModel>();
        outletIdNamePair.include = this.outletIdsNames.toApiModel().include.map(outlet =>
            (outlet.id) ? new IdNamePairModel(undefined, outlet.id) : new IdNamePairModel(outlet.name));
        outletIdNamePair.exclude = this.outletIdsNames.toApiModel().exclude.map(outlet =>
            (outlet.id) ? new IdNamePairModel(undefined, outlet.id) : new IdNamePairModel(outlet.name));

        // BE only needs the two uuid facets - should be removed in favour of a BE change
        const advMonQuery = this.advMonQuery;
        if (!facets && advMonQuery?.searchRequestJSON) {
            const advMonSearch = JSON.parse(advMonQuery.searchRequestJSON);
            advMonSearch.dateFacets = [];
            advMonSearch.facets = advMonSearch.facets?.filter(
                facet => facet === MentionSearchFacetTypesEnum.contactUuid
                    || facet === MentionSearchFacetTypesEnum.outletUuid
            );
            advMonSearch.neoFacets = [];
            const advMonSearchJson = JSON.stringify(advMonSearch);
            advMonQuery.searchRequestJSON = advMonSearchJson;
        }

        const apiModel: TargetingSearchApi = {
            id : this.id,
            name : this.name,
            createdUserId : this.createdByUserId,
            createdDate : this.createdDate,
            modifiedUserId : this.modifiedByUserId,
            modifiedDate : this.modifiedDate,
            hidden : this.hidden,
            displayGroupId: this.displayGroupId,
            isFavorited: this.isFavorited,
            isFilterSearch: this.isFilterSearch,
            expiryDate: this.expiryDate,
            query: {
                refineQuery: this.refineQuery,
                ownershipTypes: ownershipTypes,
                searchMode: this.searchMode,
                // entityTypes : entityTypes,
                subject: this.subjects.toApiModel(),
                monitoringKeywords: this.monitoringKeywords.toApiModel(),
                outletSubject: this.outletSubjects.toApiModel(),
                outletMonitoringKeywords: this.outletMonitoringKeywords.toApiModel(),
                areaOfCoverage: this.areasOfCoverage?.toApiModel(),
                geography: this.geographies.toApiModel(),
                mediaMarket: this.mediaMarkets?.toApiModel(),
                jobRole: this.jobRoles.toApiModel(),
                jobTitle: this.jobTitles.toApiModel(),
                outletIdNamePair: outletIdNamePair.toApiModel(),
                language: this.languages.toApiModel(),
                mediaType: this.mediaTypes.toApiModel(),
                productType: this.productTypes.toApiModel(),
                marketSector: this.marketSectors.toApiModel(),
                coverageFocus: this.coverageFocuses.toApiModel(),
                productFrequency: this.productFrequencies.toApiModel(),
                targetAudience: this.targetAudiences.toApiModel(),
                contactIdNamePair: contactIdNamePair.toApiModel(),
                circulationFilter:
                    (!!this.audienceReachFilter && this.audienceReachFilter.range &&
                        ((this.audienceReachFilter.range.min > 0 || this.audienceReachFilter.range.max > 0)))
                        ? JSON.parse(JSON.stringify(this.audienceReachFilter))
                        : null,
                badgeFilters: this.badgeFilters,
                list: this.list.toApiModel(),
                distribution: this.distribution.toApiModel(),
                rectangleQuery: this.rectangleQuery,
                hasActiveNotices: this.hasActiveNotices,
                advMonQuery: advMonQuery,
            }
        };

        if (facets) {
            apiModel.query.facets = Object.values(TargetingSearchFacetsEnum);
        }

        if (filters) {
            filters.searchMode = this.searchMode;
            apiModel.query.filterQuery = filters.toApiModel().query;
        }

        if (this.groupByOutlet) {
            apiModel.query.groupBy = GroupByEnum.OUTLET;
        } else {
            apiModel.query.groupBy = GroupByEnum.NONE;
        }

        return apiModel;
    }

    clone(keepIdsAndNames: boolean = true): TargetingSearch {
        const clone = new TargetingSearch(this.toApiModel());

        if (keepIdsAndNames) {
            clone.contactIdsNames = this.contactIdsNames?.clone();
            clone.outletIdsNames = this.outletIdsNames?.clone();
        }

        return clone;
    }

    equals(other?: TargetingSearch) {
        if (!other) {
            return false;
        }

        for (const field of this.simpleCompareFields) {
            if (this[field] !== other[field]) {
                return false;
            }
        }

        for (const field of this.includeExcludeCompareFields) {
            const left = <IncludeExcludeModel<any>>this[field];
            const right = <IncludeExcludeModel<any>>other[field];

            if (left !== right && !left.equals(right)) {
                return false;
            }
        }

        if (this.audienceReachFilter) {
            if (!other.audienceReachFilter) {
                return false;
            } else {
                if (this.audienceReachFilter.type !== other.audienceReachFilter.type ||
                    this.audienceReachFilter.range?.min !== other.audienceReachFilter.range?.min ||
                    this.audienceReachFilter.range?.max !== other.audienceReachFilter.range?.max) {
                    return false;
                }
            }
        } else if (other.audienceReachFilter) {
            return false;
        }

        return true;
    }

    /**
     * Builds a flat true/false map of which filters are currently populated, keyed by filter name.
     * Used for analytics (e.g. Pendo) to see which filters were in use when a search was run or saved.
     */
    getUsedFiltersSummary(): Record<string, boolean> {
        const summary: Record<string, boolean> = {};

        for (const field of this.searchRefinementCompareFields) {
            summary[field] = !(<IncludeExcludeModel<any>>this[<keyof TargetingSearch>field]).isEmpty();
        }

        summary.audienceReach = this.audienceReachFilter?.type && (!!this.audienceReachFilter.range?.min || !!this.audienceReachFilter.range?.max);
        summary.includePrivateContacts = this.includePrivateContacts;
        summary.groupByOutlet = this.groupByOutlet;

        return summary;
    }

    /**
     * Determines if the current targeting search has any user defined filters
     */
    hasAnyFilters(searchMode: SearchModeEnum): boolean {
        const other = new TargetingSearch();
        other.searchMode = searchMode;

        const temp = this.clone();
        temp.includePrivateContacts = true;
        delete temp.name;

        return !temp.equals(other);
    }

    /**
     * Merges data from one search to `this`.
     * Via immutable updates by creating new references for objects
     */
    merge(other: Partial<TargetingSearch>) {
        if (other.name) {
            this.name = other.name;
        }
        if (other.mediaTypes) {
            this.mediaTypes = new IncludeExcludeModel(other.mediaTypes.toApiModel());
        }
        if (other.productTypes) {
            this.productTypes = new IncludeExcludeModel(other.productTypes.toApiModel());
        }
        if (other.marketSectors) {
            this.marketSectors = new IncludeExcludeModel(other.marketSectors.toApiModel());
        }
        if (other.coverageFocuses) {
            this.coverageFocuses = new IncludeExcludeModel(other.coverageFocuses.toApiModel());
        }
        if (other.productFrequencies) {
            this.productFrequencies = new IncludeExcludeModel(other.productFrequencies.toApiModel());
        }
        if (other.targetAudiences) {
            this.targetAudiences = new IncludeExcludeModel(other.targetAudiences.toApiModel());
        }
        if (other.audienceReachFilter) {
            this.audienceReachFilter = {
                type: other.audienceReachFilter.type,
                range: {
                    min: other.audienceReachFilter?.range?.min,
                    max: other.audienceReachFilter?.range?.max
                }
            };
        }
        if (other.hasOwnProperty('includePrivateContacts')) {
            this.includePrivateContacts = other.includePrivateContacts;
        }
        if (other.contactIdsNames) {
            this.contactIdsNames = new IncludeExcludeModel(other.contactIdsNames.toApiModel());
        }
        if (other.monitoringKeywords) {
            this.monitoringKeywords = new IncludeExcludeModel(other.monitoringKeywords.toApiModel());
        }
        if (other.subjects) {
            this.subjects = new IncludeExcludeModel(other.subjects.toApiModel());
        }
        if (other.geographies) {
            this.geographies = new IncludeExcludeModel(other.geographies.toApiModel());
        }
        if (other.jobTitles) {
            this.jobTitles = new IncludeExcludeModel(other.jobTitles.toApiModel());
        }
        if (other.jobRoles) {
            this.jobRoles = new IncludeExcludeModel(other.jobRoles.toApiModel());
        }
        if (other.languages) {
            this.languages = new IncludeExcludeModel(other.languages.toApiModel());
        }
        if (other.outletIdsNames) {
            this.outletIdsNames = new IncludeExcludeModel(other.outletIdsNames.toApiModel());
        }
        if (other.outletMonitoringKeywords) {
            this.outletMonitoringKeywords = new IncludeExcludeModel(other.outletMonitoringKeywords.toApiModel());
        }
        if (other.outletSubjects) {
            this.outletSubjects = new IncludeExcludeModel(other.outletSubjects.toApiModel());
        }
        if (other.areasOfCoverage) {
            this.areasOfCoverage = new IncludeExcludeModel(other.areasOfCoverage.toApiModel());
        }
        if (other.mediaMarkets) {
            this.mediaMarkets = new IncludeExcludeModel(other.mediaMarkets.toApiModel());
        }
        if (other.hasOwnProperty('groupByOutlet')) {
            if (other.groupByOutlet) {
                this.groupByOutlet = other.groupByOutlet;
            } else {
                this.groupByOutlet = false;
            }
        }
    }

    hasEmptyQuery(): boolean {
        return this.includeExcludeCompareFields.every(field => {
            return this[field].isEmpty();
        });
    }
}
